<script lang="ts" setup>
import type { JSONContent } from "@tiptap/vue-3";
import type { InputSize, InputVariant } from "#ui/types";
import type { FieldDefinition } from "~/models/Automation";
import type { StarterKitOptions } from "@tiptap/starter-kit";
import type { Editor } from "@tiptap/core";

type Modes = {
    html: string;
    text: string;
    json: JSONContent;
};

export interface HdInputProps {
    mode?: keyof Modes;
    textarea?: boolean;
    modelValue?: Modes[NonNullable<HdInputProps["mode"]>] | null | undefined;
    menuMode?: "icons";
    type?: string;
    id?: string;
    name?: string;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    unstyled?: boolean;
    autoRefresh?: boolean;
    autofocus?: boolean;
    snug?: boolean;
    autofocusDelay?: number;
    icon?: string;
    loadingIcon?: string;
    blockType?: "list" | "styles";
    styleType?: "list" | "styles";
    leadingIcon?: string;
    trailingIcon?: string;
    trailing?: boolean;
    leading?: boolean;
    loading?: boolean;
    padded?: boolean;
    size?: InputSize;
    color?: any;
    proseModifiers?: any;
    variant?: InputVariant;
    inputClass?: string;
    class?: any;
    ui?: any;
    modelModifiers?: {
        trim?: boolean | undefined;
        lazy?: boolean | undefined;
        number?: boolean | undefined;
    };
    extensions?: any[];
    withMenu?: boolean;
    withBubbleMenu?: boolean;
    withFloatingMenu?: boolean;
    starterKit?: Partial<StarterKitOptions>;
    stripOuterTag?: boolean;
    outerTag?: string;
    outerClass?: string | string[];
    outerStyle?: Partial<CSSStyleDeclaration>;
    fields?: FieldDefinition[];
    proseStyle?: any;
    maxWidth?: string;
    mentionMatch?: {
        between: [Prefix, Suffix];
        name: string;
        fields: FieldDefinition[];
    }[];
}

type Prefix = string;
type Suffix = string;

const props = withDefaults(defineProps<HdInputProps>(), {
    mode: "text",
    modelValue: "",
    type: "",
    variant: "outline",
    size: "sm",
    blockType: "list",
    styleType: "list",
    required: false,
    disabled: false,
    inputClass: undefined,
    autofocus: false,
    trailing: false,
    leading: false,
    loading: false,
    padded: true,
    withMenu: false,
    autoRefresh: false,
    withFloatingMenu: false,
    withBubbleMenu: false,
    color: "white",
    class: () => "",
    ui: () => ({}),
    modelModifiers: () => ({}),
    extensions: () => [] as any[],
    fields: () => [] as FieldDefinition[],
    starterKit: () => ({}),
    id: undefined,
    name: undefined,
    textarea: undefined,
    placeholder: undefined,
    autofocusDelay: undefined,
    icon: undefined,
    loadingIcon: undefined,
    leadingIcon: undefined,
    trailingIcon: undefined,
    stripOuterTag: undefined,
    outerTag: undefined,
    outerClass: undefined,
    outerStyle: undefined,
});

const emit = defineEmits<{
    (e: "update:modelValue", v: HdInputProps["modelValue"]): void;
}>();

defineOptions({
    inheritAttrs: false,
});

const inner = ref<{ editor: Editor } | null>(null);
const lastRefresh = ref<number>(Date.now());
watch(() => props.modelValue, (to, from) => {
    if (inner.value?.editor?.isFocused) {
        return;
    }

    if (!from && to) {
        lastRefresh.value = Date.now();
        return;
    }

    if (!props.autoRefresh) return;

    if (typeof to !== typeof from) {
        lastRefresh.value = Date.now();
        return;
    }

    if (typeof to === "object" && typeof from === "object") {
        if (!isEqual(useCloneDeep(to), useCloneDeep(from))) {
            lastRefresh.value = Date.now();
        }
        return;
    }

    if (typeof to === "string" && typeof from === "string") {
        if (to !== from) {
            lastRefresh.value = Date.now();
        }
        return;
    }
}, {
    deep: true
});
</script>

<template>
    <client-only>
        <HdInputInner ref="inner" :key="lastRefresh" :id="id" :autofocus="autofocus" :autofocus-delay="autofocusDelay"
            :block-type="blockType" :class="props.class" :color="color" :disabled="disabled"
            :prose-modifiers="proseModifiers" :extensions="extensions" :fields="fields" :icon="icon"
            :unstyled="unstyled" :input-class="inputClass" :leading="leading" :mention-match="mentionMatch"
            :leading-icon="leadingIcon" :loading="loading" :loading-icon="loadingIcon" :menuMode="menuMode" :mode="mode"
            :model-modifiers="modelModifiers" :model-value="modelValue" :name="name" :outer-class="outerClass"
            :outer-style="outerStyle" :outer-tag="outerTag" :padded="padded" :placeholder="placeholder"
            :required="required" :size="size" :starter-kit="starterKit" :strip-outer-tag="stripOuterTag" :snug="snug"
            :style-type="styleType" :textarea="textarea" :trailing="trailing" :trailing-icon="trailingIcon" :type="type"
            :ui="ui" :variant="variant" :prose-style="proseStyle" :with-bubble-menu="withBubbleMenu"
            :with-floating-menu="withFloatingMenu" :with-menu="withMenu" v-bind="$attrs"
            @update:model-value="emit('update:modelValue', $event)">
            <template v-if="$slots['pre-content']" #pre-content="{ editor }">
                <slot :editor="editor" name="pre-content" />
            </template>
            <template v-if="$slots['post-content']" #post-content="{ editor }">
                <slot :editor="editor" name="post-content" />
            </template>
            <template v-if="$slots['left-menu-start']" #left-menu-start="{ editor }">
                <slot :editor="editor" name="left-menu-start" />
            </template>
            <template v-if="$slots['left-menu-end']" #left-menu-end="{ editor }">
                <slot :editor="editor" name="left-menu-end" />
            </template>
            <template v-if="$slots['right-menu-start']" #right-menu-start="{ editor }">
                <slot :editor="editor" name="right-menu-start" />
            </template>
            <template v-if="$slots['right-menu-end']" #right-menu-end="{ editor }">
                <slot :editor="editor" name="right-menu-end" />
            </template>
            <template v-if="$slots['full-bubble-menu']" #full-bubble-menu="{ editor }">
                <slot :editor="editor" name="full-bubble-menu" />
            </template>
            <template v-if="$slots['leading']" #leading="{ disabled, loading }">
                <slot :disabled="disabled" :loading="loading" name="leading" />
            </template>
            <template v-if="$slots['trailing']" #trailing="{ disabled, loading }">
                <slot :disabled="disabled" :loading="loading" name="trailing" />
            </template>
            <template v-if="$slots['bubble-menu']" #bubble-menu="{ editor }">
                <slot :editor="editor" name="bubble-menu" />
            </template>
            <template #default>
                <slot />
            </template>
        </HdInputInner>
    </client-only>
</template>

<style>
.hd-field-variable {
    @apply border rounded-md border-gray-300 px-1.5 py-0.5;
}

.disabled img.ProseMirror-separator {
    display: none !important;
}
</style>
